import React, { useContext, useState, useEffect, useRef } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from "react-router-dom";
import QSidebar from './QSidebar';
import gps from '../assets/gps.svg';
import { QuoteContext } from '../assets/context/QuoteContext';
import EDDDark from '../assets/EDD-dark.svg';
import CollectionDateDark from '../assets/Box-dark.svg';
import WarrantyDark from '../assets/warranty-dark.svg';
import EditModal from './EditModal';
import ReceiverEditModal from './ReceiverEditModal';
import { useAuth } from './../assets/context/AuthContext';
import url from './url';
import DraftSaveControl from './DraftSaveControl';
import { registerAccount } from './registerAccount';

function Review() {
    const navigate = useNavigate();
    const { SRDetails, quoteRequestBody, quoteData, selectedQuote, formData, deleteDraft, saveSRDetails } = useContext(QuoteContext);
    const emptyContact = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        pickupInstructions: '',
    };
    const [senderData, setSenderData] = useState(SRDetails?.senderFormData || emptyContact);
    const [receiverData, setReceiverData] = useState(SRDetails?.receiverFormData || emptyContact);
    const [insuranceChoice, setInsuranceChoice] = useState(SRDetails?.insurance || null);
    const [serverPricing, setServerPricing] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [accountError, setAccountError] = useState('');
    const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
    const [isAccountSubmitting, setIsAccountSubmitting] = useState(false);
    const [accountModalMode, setAccountModalMode] = useState('create');
    const [loginData, setLoginData] = useState({ Email: '', Password: '' });
    const [loginError, setLoginError] = useState('');
    const [cardState, setCardState] = useState({ complete: false, error: '' });
    const [billingPostcode, setBillingPostcode] = useState('');
    const processingOverlayRef = useRef(null);
    const stripe = useStripe();
    const elements = useElements();

    useEffect(() => {
        if (!isProcessing) return undefined;
        document.body.classList.add('payment-is-processing');
        processingOverlayRef.current?.focus();
        const preventInteraction = (event) => event.preventDefault();
        document.addEventListener('keydown', preventInteraction, true);
        return () => {
            document.body.classList.remove('payment-is-processing');
            document.removeEventListener('keydown', preventInteraction, true);
        };
    }, [isProcessing]);

    useEffect(() => {
        if (SRDetails?.senderFormData) setSenderData(SRDetails.senderFormData);
    }, [SRDetails]);

    // Handle sender data updates from modal
    const handleSenderUpdate = (updatedSenderData) => {
        setSenderData(updatedSenderData);
    };

    useEffect(() => {
        if (SRDetails?.receiverFormData) setReceiverData(SRDetails.receiverFormData);
    }, [SRDetails]);

    useEffect(() => {
        setInsuranceChoice(SRDetails?.insurance || null);
    }, [SRDetails]);

    // Handle receiver data updates from modal
    const handleReceiverUpdate = (updatedReceiverData) => {
        setReceiverData(updatedReceiverData);
    };
    // Card element styling
    const cardStyle = {
        hidePostalCode: true,
        style: {
            base: {
                fontSize: '16px',
                color: '#32325d',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#fa755a',
                iconColor: '#fa755a',
            },
        },
    };

    // handling user account Creation 

    const [accountData, setAccountData] = useState({
        Name: '',
        Email: '',
        Password: '',
    });

    const { login } = useAuth()
    const handleAccountChange = (e) => {
        const { name, value } = e.target;
        setAccountData({ ...accountData, [name]: value });
        if (accountError) setAccountError('');
    };

    const handleLoginChange = (e) => {
        const { name, value } = e.target;
        setLoginData({ ...loginData, [name]: value });
        if (loginError) setLoginError('');
    };

    const handleCardChange = (event) => {
        setCardState({
            complete: event.complete,
            error: event.error?.message || '',
        });

        if (event.complete && (paymentStatus.startsWith('Enter your card') || paymentStatus.includes('incomplete'))) {
            setPaymentStatus('');
        }
    };

    const validateCardDetails = () => {
        const cardElement = elements?.getElement(CardElement);

        if (!/^\d{4,5}$/.test(billingPostcode)) {
            setPaymentStatus('Enter a valid 4 or 5 digit billing postcode.');
            return false;
        }

        if (!cardElement || !cardState.complete) {
            setPaymentStatus(cardState.error || 'Enter your complete card details to continue.');
            cardElement?.focus();
            return false;
        }

        return true;
    };

    // Check if user is already logged in
    const handleAccountCreation = async () => {
        // Check if user is already logged in
        const existingToken = localStorage.getItem('token');
        const existingCustId = localStorage.getItem('Cust_id');

        // If already logged in, return existing customer ID
        if (existingToken && existingCustId) {
            return existingCustId;
        }

        try {
            const data = await registerAccount(accountData);

            // Use the login method from AuthContext
            login(data.token, data.Cust_Id);

            return data.Cust_Id;
        } catch (error) {
            console.error('Account creation error:', error);
            throw error;
        }
    };



    const sendBookingData = async (Cust_id, paymentIntentId, bookingSRDetails = SRDetails) => {
        try {

            const bookingData = {
                SRDetails: bookingSRDetails, // Contains sender/receiver details
                quoteRequestBody,   // Contains request-specific details
                quoteData,          // Contains all quote details
                selectedQuote,      // Contains the selected quote for booking
                Cust_id,
                formData,
                paymentIntentId
            };

            const response = await fetch(`${url}/api/booking`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                },
                body: JSON.stringify(bookingData),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to create account');
            }

            return data.bookingId; // Return the customer ID for further use
        } catch (error) {
            console.error('Account creation error:', error);
            throw error;
        }
    };

    const startPayment = async (customerId) => {
        if (!stripe || !elements) {
            setPaymentStatus('Payment is still loading. Please try again in a moment.');
            return;
        }

        if (insuranceChoice && (
            !Number.isFinite(Number(insuranceChoice.coverage))
            || Number(insuranceChoice.coverage) <= 0
            || !insuranceChoice.category
            || !insuranceChoice.condition
            || insuranceChoice.premium === null
            || insuranceChoice.premium === undefined
            || insuranceChoice.premium === ''
            || !Number.isFinite(Number(insuranceChoice.premium))
            || Number(insuranceChoice.premium) < 0
        )) {
            setPaymentStatus('The insurance price is not current. Return to shipment details and calculate a fresh insurance price before paying.');
            return;
        }

        if (!validateCardDetails()) {
            return;
        }

        setIsProcessing(true);
        setPaymentStatus('Processing payment...');
        let paymentSucceeded = false;
        let bookingId = null;
        let bookingSRDetails = SRDetails;

        try {
            // Step 1: Process Payment FIRST (before creating booking)
            const response = await fetch(`${url}/api/create-payment-intent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                },
                body: JSON.stringify({
                    quoteRequestBody,
                    carrierServiceId: selectedQuote.carrierServiceId,
                    insurance: insuranceChoice
                        ? {
                            coverage: insuranceChoice.coverage,
                            category: insuranceChoice.category,
                            condition: insuranceChoice.condition,
                        }
                        : null,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || 'Failed to create payment intent.');
            }

            const paymentData = await response.json();
            const serverAmount = Number(paymentData.amount);
            const serverShippingAmount = Number(paymentData.shippingAmount);
            const serverInsurancePremium = Number(paymentData.insurancePremium);
            if (!paymentData.clientSecret
                || paymentData.amount === null || paymentData.amount === undefined || paymentData.amount === ''
                || paymentData.shippingAmount === null || paymentData.shippingAmount === undefined || paymentData.shippingAmount === ''
                || paymentData.insurancePremium === null || paymentData.insurancePremium === undefined || paymentData.insurancePremium === ''
                || !Number.isFinite(serverAmount) || serverAmount < 0
                || !Number.isFinite(serverShippingAmount) || serverShippingAmount < 0
                || !Number.isFinite(serverInsurancePremium) || serverInsurancePremium < 0) {
                throw new Error('The payment service did not return a complete, valid price. Please try again.');
            }
            if (!insuranceChoice && serverInsurancePremium > 0) {
                throw new Error('The payment service returned an insurance charge for a shipment without selected insurance. No payment was taken; please contact support.');
            }

            const displayedShippingAmount = serverPricing?.shippingAmount ?? Number(selectedQuote.price || 0);
            const displayedInsurancePremium = insuranceChoice
                ? (serverPricing?.insurancePremium ?? Number(insuranceChoice.premium))
                : 0;
            const displayedTotal = serverPricing?.amount
                ?? (displayedShippingAmount + displayedInsurancePremium);
            const shippingChanged = Math.round(serverShippingAmount * 100) !== Math.round(displayedShippingAmount * 100);
            const insurancePremiumChanged = Math.round(serverInsurancePremium * 100) !== Math.round(displayedInsurancePremium * 100);
            const totalChanged = Math.round(serverAmount * 100) !== Math.round(displayedTotal * 100);
            if (shippingChanged || insurancePremiumChanged || totalChanged) {
                const updatedPricing = {
                    amount: serverAmount,
                    shippingAmount: serverShippingAmount,
                    insurancePremium: serverInsurancePremium,
                };
                setServerPricing(updatedPricing);
                if (insuranceChoice) {
                    const updatedInsurance = { ...insuranceChoice, premium: serverInsurancePremium };
                    setInsuranceChoice(updatedInsurance);
                    bookingSRDetails = {
                        ...SRDetails,
                        senderFormData: senderData,
                        receiverFormData: receiverData,
                        insurance: updatedInsurance,
                    };
                    saveSRDetails(bookingSRDetails);
                }
                setPaymentStatus(`The shipment price changed to $${serverAmount.toFixed(2)} AUD. Review the updated shipping and insurance amounts, then select Pay again to accept this price. No payment was taken.`);
                return;
            }

            setServerPricing({
                amount: serverAmount,
                shippingAmount: serverShippingAmount,
                insurancePremium: serverInsurancePremium,
            });
            if (insuranceChoice) {
                const updatedInsurance = { ...insuranceChoice, premium: serverInsurancePremium };
                setInsuranceChoice(updatedInsurance);
                bookingSRDetails = {
                    ...SRDetails,
                    senderFormData: senderData,
                    receiverFormData: receiverData,
                    insurance: updatedInsurance,
                };
                saveSRDetails(bookingSRDetails);
            }

            const result = await stripe.confirmCardPayment(paymentData.clientSecret, {
                payment_method: {
                    card: elements.getElement(CardElement),
                    billing_details: {
                        name: senderData.firstName,
                        email: senderData.email,
                        address: {
                            country: 'AU',
                            postal_code: billingPostcode,
                        },
                    },
                },
            });

            if (result.error) {
                setPaymentStatus(`Payment failed: ${result.error.message}`);
                return;
            }

            if (result.paymentIntent?.status !== 'succeeded') {
                setPaymentStatus(`Payment status: ${result.paymentIntent?.status || 'incomplete'}. Please try again.`);
                return;
            }

            paymentSucceeded = true;
            setPaymentStatus('Payment successful! Creating your booking…');

            // Step 2: Create the booking for the authenticated customer
            bookingId = await sendBookingData(customerId, result.paymentIntent.id, bookingSRDetails);

            // Step 3: Update payment status with payment intent ID
            const updateResponse = await fetch(`${url}/api/booking/update-payment-status/${bookingId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                },
                body: JSON.stringify({
                    paymentIntentId: result.paymentIntent.id
                }),
            });

            if (!updateResponse.ok) {
                throw new Error('We could not confirm the payment on your booking.');
            }

            const bookingResponse = await fetch(`${url}/api/booking/create-booking`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                },
                body: JSON.stringify({
                    bookingId,
                    SRDetails: bookingSRDetails,
                    quoteRequestBody,
                    selectedQuote
                })
            });

            if (!bookingResponse.ok) {
                const errorData = await bookingResponse.json().catch(() => ({}));
                const bookingError = new Error(errorData.message || 'We could not complete your booking.');
                bookingError.confirmationStatus = errorData.confirmationStatus;
                bookingError.bookingRejected = errorData.error === 'courier_rejected'
                    || errorData.confirmationStatus === 'rejected';
                throw bookingError;
            }

            const bookingData = await bookingResponse.json();
            await deleteDraft().catch((draftError) => {
                console.error('Draft cleanup error:', draftError);
            });
            setPaymentStatus('Payment successful! Redirecting to your bookings…');
            setIsProcessing(false);
            navigate('/Bookings', {
                replace: true,
                state: { bookingData, insuranceSelected: Boolean(insuranceChoice) },
            });
        } catch (error) {
            console.error('Payment Error:', error);
            if (paymentSucceeded) {
                const bookingRejected = Boolean(error.bookingRejected);
                setPaymentStatus(bookingRejected
                    ? 'Payment received, but the courier rejected the booking. Do not pay again.'
                    : 'Payment received! Redirecting while we finalize your booking…');
                navigate('/Bookings', {
                    replace: true,
                    state: {
                        bookingId,
                        bookingPending: !bookingRejected,
                        bookingRejected,
                        confirmationStatus: error.confirmationStatus,
                        bookingMessage: error.message,
                        insuranceSelected: Boolean(insuranceChoice),
                    },
                });
                return;
            }
            setPaymentStatus(`Error: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleAccountModalSubmit = async (e) => {
        e.preventDefault();
        setAccountError('');

        if (!accountData.Name.trim() || !accountData.Email.trim() || !accountData.Password) {
            setAccountError('Enter your name, email, and password to continue.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountData.Email.trim())) {
            setAccountError('Enter a valid email address to continue.');
            return;
        }
        if (accountData.Password.length < 8) {
            setAccountError('Your password must be at least 8 characters.');
            return;
        }

        setIsAccountSubmitting(true);
        try {
            const customerId = await handleAccountCreation();
            setIsAccountModalOpen(false);
            await startPayment(customerId);
        } catch (error) {
            setAccountError(error.message || 'We could not create your account. Please try again.');
        } finally {
            setIsAccountSubmitting(false);
        }
    };

    const handleLoginModalSubmit = async (e) => {
        e.preventDefault();
        setLoginError('');

        if (!loginData.Email.trim() || !loginData.Password) {
            setLoginError('Enter your email and password to continue.');
            return;
        }

        setIsAccountSubmitting(true);
        try {
            const response = await fetch(`${url}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginData),
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'We could not sign you in.');
            }

            login(data.token, data.Cust_Id);
            setIsAccountModalOpen(false);
            await startPayment(data.Cust_Id);
        } catch (error) {
            setLoginError(error.message || 'We could not sign you in. Please try again.');
        } finally {
            setIsAccountSubmitting(false);
        }
    };

    const handlePayment = async (e) => {
        e.preventDefault();

        const isLoggedIn = Boolean(localStorage.getItem('token') && localStorage.getItem('Cust_id'));
        if (!isLoggedIn) {
            setAccountError('');
            setIsAccountModalOpen(true);
            return;
        }

        await startPayment(localStorage.getItem('Cust_id'));
    };


    if (!selectedQuote || !SRDetails) {
        return (
            <div className="container review-empty">
                <div className="review-empty-card">
                    <span className="section-kicker">CHECKOUT PAUSED</span>
                    <h1>Your shipment details<br /><em>aren’t ready yet.</em></h1>
                    <p>Start with a quote and choose a carrier before coming back to review and pay.</p>
                    <button className="btn btn-rnd" onClick={() => navigate('/CalQuote')}>Start a quote <span aria-hidden="true">↗</span></button>
                </div>
            </div>
        );
    }

    const fromLocation = `${formData?.pickupCity || 'Sydney'} ${formData?.pickupPostcode || '2000'}${formData?.pickupState ? `, ${formData.pickupState}` : ''}`;
    const toLocation = `${formData?.deliveryCity || 'Melbourne'} ${formData?.deliveryPostcode || '3000'}${formData?.deliveryState ? `, ${formData.deliveryState}` : ''}`;
    const displayedShipping = serverPricing?.shippingAmount ?? Number(selectedQuote.price || 0);
    const insurancePriceReady = !insuranceChoice || (
        insuranceChoice.premium !== null
        && insuranceChoice.premium !== undefined
        && insuranceChoice.premium !== ''
        && Number.isFinite(Number(insuranceChoice.premium))
        && Number(insuranceChoice.premium) >= 0
    );
    const displayedInsurancePremium = insuranceChoice
        ? (serverPricing?.insurancePremium ?? (insurancePriceReady ? Number(insuranceChoice.premium) : null))
        : 0;
    const displayedTotal = serverPricing?.amount ?? (displayedShipping + (displayedInsurancePremium ?? 0));
    const formattedPrice = insurancePriceReady ? Number(displayedTotal).toFixed(2) : '—';

    const ContactReview = ({ type, data, location, instruction, onEdit }) => (
        <section className="review-contact-card">
            <div className="review-card-heading">
                <div className="review-card-title"><span className="review-card-number">{type === 'Pickup' ? '01' : '02'}</span><div><span className="section-kicker">{type === 'Pickup' ? 'ORIGIN' : 'DESTINATION'}</span><h2>{type} details</h2></div></div>
                <button className="review-edit-btn" type="button" onClick={onEdit}>Edit</button>
            </div>
            <div className="review-contact-grid">
                <div className="review-location"><span className="review-label"><img src={gps} alt="" /> {type === 'Pickup' ? 'From' : 'To'}</span><strong>{location}</strong><p>{data.address1 || 'Address line 1'}</p><p>{data.address2 || 'Address line 2'}</p></div>
                <div className="review-person"><span className="review-label">CONTACT</span><strong>{data.firstName || 'Contact name'} {data.lastName || ''}</strong><p>{data.email || 'Email address'}</p><p>{data.phone || 'Phone number'}</p></div>
                <div className="review-timing"><span className="review-label">{type === 'Pickup' ? 'PICKUP WINDOW' : 'DELIVERY WINDOW'}</span><strong>{type === 'Pickup' ? '24 Oct, 10:30 am' : '29 Oct, 10:30 am'}</strong><p>{type === 'Pickup' ? 'Residential address' : 'Residential address'}</p></div>
            </div>
            <div className="review-instructions"><span>Instructions</span><p>{instruction || 'No special instructions added.'}</p></div>
        </section>
    );

    return (
        <main className="review-page">
            <div className="container">
                <header className="review-header">
                    <div><span className="section-kicker">FINAL STEP</span><h1>Review <em>& pay.</em></h1><p>Make sure everything looks right, then securely pay for your shipment.</p></div>
                    <div className="checkout-steps" aria-label="Checkout progress"><span className="is-complete"><b>01</b> Quote</span><i></i><span className="is-complete"><b>02</b> Details</span><i></i><span className="is-current"><b>03</b> Payment</span></div>
                </header>

                <div className="review-layout">
                    <div className="review-main">
                        <section className="delivery-summary">
                            <div className="summary-route"><span>{fromLocation}</span><i>→</i><span>{toLocation}</span></div>
                            <div className="summary-facts">
                                <div><img src={EDDDark} alt="" /><span>Estimated delivery<strong>{selectedQuote.estimatedDelivery}</strong></span></div>
                                <div><img src={CollectionDateDark} alt="" /><span>Collection<strong>{selectedQuote.collectionDate}</strong></span></div>
                                {selectedQuote.warranty !== null && selectedQuote.warranty !== undefined && selectedQuote.warranty !== '' && (
                                    <div><img src={WarrantyDark} alt="" /><span>Warranty<strong>{selectedQuote.warranty} included</strong></span></div>
                                )}
                            </div>
                        </section>

                        <ContactReview type="Pickup" data={senderData} location={fromLocation} instruction={senderData.pickupInstructions} onEdit={() => setEditingContact('sender')} />
                        <EditModal senderData={senderData} isOpen={editingContact === 'sender'} onClose={() => setEditingContact(null)} onUpdateSender={handleSenderUpdate} />
                        <ContactReview type="Delivery" data={receiverData} location={toLocation} instruction={receiverData.pickupInstructions} onEdit={() => setEditingContact('receiver')} />
                        <ReceiverEditModal receiverData={receiverData} isOpen={editingContact === 'receiver'} onClose={() => setEditingContact(null)} onUpdateReceiver={handleReceiverUpdate} />

                        <section className="payment-section">
                            <div className="review-card-heading"><div className="review-card-title"><span className="review-card-number">03</span><div><span className="section-kicker">SECURE CHECKOUT</span><h2>Payment details</h2></div></div><span className="secure-badge">⌁ Secure payment</span></div>
                            <p className="review-section-copy">Your card details are encrypted and handled securely by Stripe.</p>
                            <form onSubmit={handlePayment}>
                                <div className="payment-cost-breakdown" aria-label="Shipment price breakdown">
                                    <div><span>Shipping</span><strong>${Number(displayedShipping).toFixed(2)} AUD</strong></div>
                                    {insuranceChoice ? (
                                        <div className="payment-insurance-line">
                                            <span>
                                                Insurance selected · coverage ${Number(insuranceChoice.coverage).toFixed(2)} AUD
                                                <small>{insuranceChoice.category} · {insuranceChoice.condition} · provider must apply cover; it is not yet confirmed</small>
                                            </span>
                                            <strong>{displayedInsurancePremium === null ? 'Price needs refresh' : `$${Number(displayedInsurancePremium).toFixed(2)} AUD`}</strong>
                                        </div>
                                    ) : (
                                        <div><span>Optional insurance</span><strong>Not selected</strong></div>
                                    )}
                                </div>
                                <div className="card-element-container"><CardElement options={cardStyle} onChange={(event) => handleCardChange(event)} /></div>
                                <label className="billing-postcode-field">
                                    Billing postcode
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="postal-code"
                                        placeholder="4 or 5 digits"
                                        value={billingPostcode}
                                        maxLength={5}
                                        onChange={(event) => {
                                            setBillingPostcode(event.target.value.replace(/\D/g, '').slice(0, 5));
                                            if (paymentStatus.includes('billing postcode')) setPaymentStatus('');
                                        }}
                                        aria-describedby="billing-postcode-help"
                                    />
                                    <small id="billing-postcode-help">Australian 4-digit and international 5-digit postcodes are accepted.</small>
                                </label>
                                <div className="payment-action-row"><div><span className="payment-total-label">Total today</span><strong>{insurancePriceReady ? `$${formattedPrice} ` : 'Price required '}<small>{insurancePriceReady ? 'AUD' : ''}</small></strong></div><button type="submit" className="btn btn-pay" aria-busy={isProcessing} disabled={isProcessing || !stripe || !insurancePriceReady}>{isProcessing && <span className="payment-spinner" aria-hidden="true"></span>}{isProcessing ? 'Processing payment…' : insurancePriceReady ? `Pay $${formattedPrice} AUD` : 'Insurance price required'} {!isProcessing && insurancePriceReady && <span aria-hidden="true">↗</span>}</button></div>
                                {!insurancePriceReady && <p className="shipment-protection-message is-error" role="alert">Your selected insurance has no current quoted premium. Return to shipment details and calculate a fresh price before paying.</p>}
                                {paymentStatus && <div className={`mt-3 alert ${paymentStatus.includes('successful') ? 'alert-success' : paymentStatus.toLowerCase().includes('processing') || paymentStatus.toLowerCase().includes('creating') ? 'alert-info' : paymentStatus.toLowerCase().includes('changed') ? 'alert-warning' : 'alert-danger'}`} role="status">{paymentStatus}</div>}
                            </form>
                            <p className="payment-footnote">By paying, you agree to CourierLink’s booking and cancellation terms.</p>
                        </section>
                        <DraftSaveControl
                            currentStep="review"
                            overrides={{
                                formData,
                                srDetails: { senderFormData: senderData, receiverFormData: receiverData, insurance: insuranceChoice }
                            }}
                        />
                    </div>
                    <aside className="review-aside">
                        <QSidebar />
                        <div className="review-trust"><span className="status-dot"></span><div><strong>Book with confidence</strong><p>Protected payment · Live tracking · Human support</p></div></div>
                    </aside>
                </div>
            </div>
            {isProcessing && (
                <div
                    ref={processingOverlayRef}
                    className="payment-processing-overlay"
                    role="alertdialog"
                    aria-modal="true"
                    aria-busy="true"
                    aria-label="Payment processing"
                    tabIndex="-1"
                >
                    <div className="payment-processing-card">
                        <span className="payment-processing-spinner" aria-hidden="true"></span>
                        <span className="section-kicker">PLEASE WAIT</span>
                        <h2>Processing your <em>payment.</em></h2>
                        <p>We’re securely confirming your payment and booking. Please don’t refresh or close this window.</p>
                    </div>
                </div>
            )}
            {isAccountModalOpen && (
                <div className="cw-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !isAccountSubmitting && setIsAccountModalOpen(false)}>
                    <div className="cw-modal cw-account-modal" role="dialog" aria-modal="true" aria-labelledby="account-modal-title">
                        <div className="cw-modal-header">
                            <div><span className="section-kicker">{accountModalMode === 'login' ? 'WELCOME BACK' : 'ONE LAST STEP'}</span><h2 id="account-modal-title">{accountModalMode === 'login' ? <>Sign in<br /><em>then we’ll pay.</em></> : <>Create your account<br /><em>then we’ll pay.</em></>}</h2></div>
                            <button type="button" className="cw-modal-close" onClick={() => setIsAccountModalOpen(false)} aria-label="Close dialog" disabled={isAccountSubmitting}>×</button>
                        </div>
                        <form onSubmit={accountModalMode === 'login' ? handleLoginModalSubmit : handleAccountModalSubmit}>
                            <div className="cw-modal-body">
                                <p className="cw-modal-copy">{accountModalMode === 'login' ? 'Sign in to your CourierLink account and we’ll return you to payment.' : 'Save this shipment to your account before payment. It only takes a moment.'}</p>
                                {accountModalMode === 'login' ? (
                                    <div className="cw-form-grid">
                                        <label className="cw-form-span-2">Email address<input type="email" name="Email" placeholder="you@example.com" value={loginData.Email} onChange={handleLoginChange} autoFocus /></label>
                                        <label className="cw-form-span-2">Password<input type="password" name="Password" placeholder="Your password" value={loginData.Password} onChange={handleLoginChange} /></label>
                                         <button type="button" className="review-inline-link auth-modal-reset" onClick={() => { setIsAccountModalOpen(false); navigate('/ForgotPassword'); }}>Forgot password?</button>
                                    </div>
                                ) : (
                                    <div className="cw-form-grid">
                                        <label className="cw-form-span-2">Your name<input type="text" name="Name" placeholder="Full name" value={accountData.Name} onChange={handleAccountChange} autoFocus /></label>
                                        <label className="cw-form-span-2">Email address<input type="email" name="Email" placeholder="you@example.com" value={accountData.Email} onChange={handleAccountChange} /></label>
                                        <label className="cw-form-span-2">Password<input type="password" name="Password" placeholder="At least 8 characters" value={accountData.Password} onChange={handleAccountChange} minLength="8" /></label>
                                    </div>
                                )}
                                {(accountModalMode === 'login' ? loginError : accountError) && <p className="account-error" role="alert">{accountModalMode === 'login' ? loginError : accountError}</p>}
                                <p className="cw-login-prompt">{accountModalMode === 'login' ? 'New to CourierLink?' : 'Already have an account?'} <button type="button" className="review-inline-link" onClick={() => { setAccountModalMode(accountModalMode === 'login' ? 'create' : 'login'); setAccountError(''); setLoginError(''); }}>{accountModalMode === 'login' ? 'Create an account' : 'Log in instead'}</button></p>
                            </div>
                            <div className="cw-modal-footer">
                                <button type="button" className="btn btn-stroked" onClick={() => setIsAccountModalOpen(false)} disabled={isAccountSubmitting}>Not yet</button>
                                <button type="submit" className="btn btn-rnd" disabled={isAccountSubmitting}>{isAccountSubmitting ? (accountModalMode === 'login' ? 'Signing in…' : 'Creating account…') : (accountModalMode === 'login' ? 'Sign in & continue to pay' : 'Create & continue to pay')} <span aria-hidden="true">↗</span></button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </main>
    )
}

export default Review
